import { useState, useEffect } from 'react';
import { Table, Button, Radio, Input, Modal, Popconfirm, message, Select, InputNumber } from 'antd';
import { PlusSquareOutlined } from '@ant-design/icons';
import { MenuApiItem } from '@/interface/menu';
import Https from '@/https';
import { pageList } from '@/views/Company/PlatformMenu/MenuConfig';
import './PlatformMenu.css'

export default function CompanyMenu() {
  const [menuList, setMenuList] = useState<MenuApiItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [messageApi, messageContextHolder] = message.useMessage();
  const menuColumns = [
    {
      title: '菜单名称',
      dataIndex: 'menuName',
      key: 'menuName'
    },
    {
      title: '排序',
      dataIndex: 'menuSort',
      key: 'menuSort'
    },
    {
      title: '页面描述',
      dataIndex: 'menuDescription',
      key: 'menuDescription'
    },
    {
      title: '页面功能',
      dataIndex: 'menuPermissions',
      key: 'menuPermissions',
      width: 300,
      render: (_: any, record: MenuApiItem) => {
        return (
          <span>{record.menuPermissions && record.menuPermissions.map((item: any) => item.name).join(',')}</span>
        )
      }
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right' as const,
      width: 230,
      render: (_: any, record: any) => {
        return (
          <>
            <Button color="primary" variant="link" size="small" onClick={() => handleMenuEdit(record)}>编辑</Button>
            {
              !record.fatherId ? <Button color="green" variant="link" size="small" onClick={() => handleCreateSub(record)}>添加子页面</Button> : null
            }
            <Popconfirm
              title="提示"
              description="确定删除菜单?"
              onConfirm={() => handleRemove(record)}
              okText="确定"
              cancelText="取消">
              <Button color="danger" variant="link" size="small">删除</Button>
            </Popconfirm>

          </>
        )
      }
    }
  ]

  const formatTree = (list: MenuApiItem[]): MenuApiItem[] => {
    let arr: MenuApiItem[] = [];
    list.forEach(item => {
      if(!item.fatherId) {
        item.children = [];
        arr.push(item);
      }
    })
    list.forEach(item => {
      if(item.fatherId) {
        arr.forEach(a  => {
          if(a.id === item.fatherId) {
            if(!a.children) {
              a.children = [];
            }
            a.children.push(item)
          }
        })
      }
    })
    return arr;
  }

  const getMenuList = () => {
    setLoading(true);
    Https.post({
      url: 'Api/URPM/V1/menu/list/all',
      data: {
        applicationId: 0,
      }
    }).then(res => {
      if(res.state === 1) {
        let list = formatTree(res.data);
        list.forEach(item => {
          if(item.children?.length === 0) {
            Reflect.deleteProperty(item, 'children');
          }
        })
        setMenuList(list);
      }
    }).finally(() => {
      setLoading(false);
    })
  }

  const [menuLevel, setMenuLevel] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [isIndependent, setIsIndependent] = useState(0);
  const [createMenuKey, setCreateMenuKey] = useState<string | undefined>(undefined);
  const [createMenuName, setCreateMenuName] = useState<string | undefined>(undefined);
  const [createSort, setCreateSort] = useState<number | null>(null);
  const [createTopMenu, setCreateTopMenu] = useState({} as MenuApiItem);
  const pageListChange = (value: string, option: any) => {
    setCreateMenuKey(value);
    setCreateMenuName(option.pageName);
  }

  const handleMenuCreate = () => {
    setMenuLevel(1);
    setIsCreateModalOpen(true);
  }

  const resetCreateMenuInfo = () => {
    setMenuLevel(1);
    setCreateName('');
    setIsIndependent(0);
    setCreateMenuKey(undefined);
    setCreateMenuName(undefined);
    setCreateSort(null);
  }

  const handleCreateSub = (record: MenuApiItem) => {
    setMenuLevel(2);
    setCreateTopMenu(record);
    setIsCreateModalOpen(true);
  }

  const handleRemove = (record: MenuApiItem) => {
    if(record.children && record.children?.length > 0) {
      messageApi.error('请先删除子菜单');
      return;
    }
    Https.post({
      url: 'Api/URPM/V1/Menu/Delete',
      data: {
        id: record.id,
      }
    }).then(res => {
      if(res.state === 1) {
        messageApi.success('删除成功');
        getMenuList();
      } else {
        messageApi.error(res.errorMessage || res.data.errorMessage);
      }
    })
  }

  const handleCreateOk = () => {
    if(!createName) {
      messageApi.error('菜单名称不能为空');
      return;
    }
    if(isIndependent === 1 && !createMenuKey) {
      messageApi.error('请选择页面');
      return;
    }
    if(createSort === null) {
      messageApi.error('请输入排序');
      return;
    }
    Https.post({
      url: 'Api/URPM/V1/Menu/Create',
      data: {
        menuName: createName,
        menuCode: isIndependent ? createMenuKey : '',
        menuSort: createSort,
        fatherId: menuLevel === 1 ? '' : createTopMenu.id,
        pictureUrl: '',
        menuDescription: isIndependent ? '' : createMenuName,
        applicationId: 0
      }
    }).then(res => {
      if(res.state === 1) {
        messageApi.success('创建成功');
        getMenuList();
        resetCreateMenuInfo();
        setIsCreateModalOpen(false);
      } else {
        messageApi.error(res.errorMessage || res.data.errorMessage);
      }
    })
  }

  const handleCreateCancel =() => {
    resetCreateMenuInfo();
    setIsCreateModalOpen(false);
  }

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editName, setEditName] = useState<string | undefined>(undefined);
  const [editMenuKey, setEditMenuKey] = useState<string | undefined>(undefined);
  const [editMenuName, setEditMenuName] = useState<string | undefined>(undefined);
  const [editSort, setEditSort] = useState<number | null>(null);
  const [editId, setEditId] = useState<string>('');
  const [editFatherId, setEditFatherId] = useState<string | undefined>(undefined);
  const pageEditListChange = (value: string, option: any) => {
    setEditMenuKey(value);
    setEditMenuName(option.pageName);
  }

  const handleMenuEdit = (record: MenuApiItem) => {
    setEditId(record.id);
    setEditFatherId(record.fatherId as string | undefined);
    setEditMenuKey(record.menuCode);
    setEditName(record.menuName);
    setEditMenuName(record.menuDescription);
    setEditSort(record.menuSort);
    if(record.fatherId) {
      setMenuLevel(2);
    } else {
      setMenuLevel(1);
    }
    if(record.menuCode) {
      setIsIndependent(1);
    } else {
      setIsIndependent(0);
    }
    setIsEditModalOpen(true);
  }

  const resetEditMenuInfo = () => {
    setEditName('');
    setEditMenuKey(undefined);
    setEditMenuKey(undefined);
    setMenuLevel(1);
    setEditSort(null);
    setIsIndependent(0);
    setIsEditModalOpen(false);
  }

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
    resetEditMenuInfo();
  }

  const handleEditOk = () => {
    if(!editName) {
      messageApi.error('菜单名称不能为空');
      return;
    }
    if(isIndependent === 1 && !editMenuKey) {
      messageApi.error('请选择页面');
      return;
    }
    if(editSort === null) {
      messageApi.error('请输入排序');
      return;
    }
    Https.post({
      url: 'Api/URPM/V1/Menu/Update',
      data: {
        id: editId,
        menuName: editName,
        menuCode: isIndependent ? editMenuKey : '',
        menuSort: editSort,
        fatherId: menuLevel === 1 ? '' : editFatherId,
        pictureUrl: '',
        menuDescription: isIndependent ? '' : editMenuName,
        applicationId: 0
      }
    }).then(res => {
      if(res.state === 1) {
        messageApi.success('编辑成功');
        getMenuList();
        resetEditMenuInfo();
      } else {
        messageApi.error(res.errorMessage || res.data.errorMessage);
      }
    })
  }

  useEffect(() => {
    getMenuList();
  }, [])

  return (
    <section className="Company-Menu">
      <section className="create-btn">
        <Button type="primary" onClick={handleMenuCreate} icon={<PlusSquareOutlined />}>添加一级菜单</Button>
      </section>
      <section>
        <Table
          dataSource={menuList}
          rowKey="id"
          columns={menuColumns}
          pagination={false}
          scroll={{x: 'max-content', y: 'calc(100vh - 280px)'}}
          loading={loading}
         />
      </section>
      <Modal
        title={menuLevel === 1 ? '添加一级菜单' : '添加子页面'}
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={isCreateModalOpen}
        onOk={handleCreateOk}
        onCancel={handleCreateCancel}
        destroyOnHidden={true}>
        <section className="modal-form-item">
          <span className="label required">菜单名称</span>
          <Input placeholder="请输入菜单名称" value={createName} allowClear onChange={(e) => setCreateName(e.target.value)} />
        </section>
        {
          menuLevel === 1 ?
          <section className="modal-form-item">
            <span className="label">是否独立菜单</span>
            <Radio.Group
              value={isIndependent}
              options={[
                { value: 1, label: '是' },
                { value: 0, label: '否' },
              ]}
              onChange={(e) => setIsIndependent(e.target.value)}
            />
          </section> : null
        }
        {
          (menuLevel === 1 && isIndependent) || menuLevel !== 1 ?
          <section className="modal-form-item">
            <span className="label required">打开页面</span>
            <Select
              allowClear
              placeholder="请选择页面"
              fieldNames={{ label: 'pageName', value: 'pageKey' }}
              value={createMenuKey}
              style={{ width: 200 }}
              options={pageList}
              onChange={pageListChange}
            />
          </section>: null
        }
         <section className="modal-form-item">
          <span className="label required">排序</span>
          <InputNumber min={0} value={createSort} onChange={(value) => setCreateSort(value)} />
        </section>
      </Modal>
      <Modal
        title={menuLevel === 1 ? '编辑一级菜单' : '编辑子菜单'}
        closable={{ 'aria-label': 'Custom Close Button' }}
        open={isEditModalOpen}
        onOk={handleEditOk}
        onCancel={handleEditCancel}
        destroyOnHidden={true}>
        <section className="modal-form-item">
          <span className="label required">菜单名称</span>
          <Input placeholder="请输入菜单名称" value={editName} allowClear onChange={(e) => setEditName(e.target.value)} />
        </section>
        {
          menuLevel === 1 ?
          <section className="modal-form-item">
            <span className="label">是否独立菜单</span>
            <Radio.Group
              value={isIndependent}
              options={[
                { value: 1, label: '是' },
                { value: 0, label: '否' },
              ]}
              onChange={(e) => setIsIndependent(e.target.value)}
            />
          </section> : null
        }
        {
          (menuLevel === 1 && isIndependent) || menuLevel !== 1 ?
          <section className="modal-form-item">
            <span className="label required">打开页面</span>
            <Select
              allowClear
              placeholder="请选择页面"
              fieldNames={{ label: 'pageName', value: 'pageKey' }}
              value={editMenuKey}
              style={{ width: 200 }}
              options={pageList}
              onChange={pageEditListChange}
            />
          </section>: null
        }
         <section className="modal-form-item">
          <span className="label required">排序</span>
          <InputNumber min={0} value={editSort} onChange={(value) => setEditSort(value)} />
        </section>
      </Modal>
      { messageContextHolder }
    </section>
  )
}